import { useState } from 'react'
import type { LabelTemplateResponse } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import {
  PageHeader,
  DataTable,
  Pagination,
  StatusBadge,
  ColumnSettingsButton,
  btnPrimary,
  btnSecondary,
  colors,
  input,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { LabelTemplateEditModal } from './LabelTemplateEditModal'
import { LabelTemplateImportModal } from './LabelTemplateImportModal'
import { useTableColumns } from '../../hooks/use-table-columns'
import { downloadLabelTemplate } from './label-template-download'

const DRIVER_LABELS: Record<string, string> = {
  dymo_client: 'DYMO',
  zebra_browser_print: 'Zebra',
}

function DriverBadge({ driver }: { driver: string }) {
  const isZebra = driver === 'zebra_browser_print'
  return (
    <span
      style={{
        padding: '0.2rem 0.6rem',
        borderRadius: '9999px',
        fontSize: '0.8125rem',
        fontWeight: 500,
        background: isZebra ? '#ede9fe' : '#dbeafe',
        color: isZebra ? '#5b21b6' : '#1e40af',
        whiteSpace: 'nowrap',
      }}
    >
      {DRIVER_LABELS[driver] ?? driver}
    </span>
  )
}

export function LabelTemplateListPage() {
  const [driverFilter, setDriverFilter] = useState('')
  const { data, total, page, loading, setPage, refresh } = useApiList<LabelTemplateResponse>({
    path: '/admin/label-templates',
    filters: { driver: driverFilter || undefined },
  })
  const { tableId: _, ...tableColumnProps } = useTableColumns('label-templates')

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isNew, setIsNew] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importWarning, setImportWarning] = useState<string | null>(null)

  function openEditModal(template: LabelTemplateResponse) {
    setSelectedId(template.id)
    setIsNew(false)
    setModalOpen(true)
  }

  function openNewModal() {
    setSelectedId(null)
    setIsNew(true)
    setModalOpen(true)
  }

  async function handleDownload(template: LabelTemplateResponse, e: React.MouseEvent) {
    e.stopPropagation()
    setDownloadError(null)
    const result = await downloadLabelTemplate(template.id, template.name, template.driver)
    if (!result.ok) setDownloadError(result.error)
  }

  function handleImported(unknownTags?: readonly string[]) {
    setImportWarning(
      unknownTags && unknownTags.length > 0
        ? `Imported, but this format has unrecognized placeholder tag(s): ${unknownTags.join(', ')}. Add matching variables in the template editor to use them.`
        : null,
    )
    refresh()
  }

  const columns: readonly Column<LabelTemplateResponse>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      filterable: true,
      render: (t) => <strong>{t.name}</strong>,
    },
    {
      key: 'driver',
      header: 'Driver',
      sortable: true,
      render: (t) => <DriverBadge driver={t.driver} />,
    },
    {
      key: 'paperSize',
      header: 'Paper Size',
      render: (t) => t.paperSize ?? '--',
    },
    {
      key: 'isSystem',
      header: 'System',
      render: (t) => (t.isSystem ? <StatusBadge status="in_progress" label="System" /> : null),
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (t) =>
        t.isArchived ? (
          <StatusBadge status="inactive" label="Archived" />
        ) : (
          <StatusBadge status="active" label="Active" />
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (t) => (
        <button
          type="button"
          style={{ ...btnSecondary, padding: '0.2rem 0.6rem', fontSize: '0.8125rem' }}
          onClick={(e) => void handleDownload(t, e)}
          data-testid={`download-${t.id}`}
        >
          Download
        </button>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Label Templates"
        subtitle={`${total} total`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button type="button" onClick={() => setImportModalOpen(true)} style={btnSecondary}>
              Import label format
            </button>
            <button type="button" onClick={openNewModal} style={btnPrimary}>
              New Template
            </button>
          </>
        }
      />

      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        <select
          style={{ ...input, width: '200px' }}
          value={driverFilter}
          onChange={(e) => setDriverFilter(e.target.value)}
          aria-label="Filter by driver"
        >
          <option value="">All Drivers</option>
          <option value="dymo_client">DYMO</option>
          <option value="zebra_browser_print">Zebra</option>
        </select>
      </div>

      {downloadError && (
        <div
          style={{
            marginBottom: '1rem',
            color: colors.danger,
            padding: '0.5rem 0.75rem',
            background: colors.bgError,
            border: `1px solid ${colors.borderError}`,
            borderRadius: '3px',
            fontSize: '0.875rem',
          }}
        >
          {downloadError}
        </div>
      )}

      {importWarning && (
        <div
          style={{
            marginBottom: '1rem',
            color: '#92400e',
            padding: '0.5rem 0.75rem',
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '3px',
            fontSize: '0.875rem',
          }}
          data-testid="import-warning-banner"
        >
          {importWarning}
        </div>
      )}

      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={openEditModal}
            emptyMessage="No label templates found."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}

      <LabelTemplateEditModal
        templateId={selectedId}
        isOpen={modalOpen}
        isNew={isNew}
        onClose={() => setModalOpen(false)}
        onSaved={() => {
          setModalOpen(false)
          refresh()
        }}
        onArchived={() => {
          setModalOpen(false)
          refresh()
        }}
      />

      <LabelTemplateImportModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImported={handleImported}
      />
    </div>
  )
}
